import {RawLocation} from 'vue-router';
import moment from 'moment';
import store from '@/store';
import {NavigationDrawerAccess} from '@/enums/_common/NavigationDrawerAccess.enum';
import {LoginTypes} from '@/enums/LoginTypes.enum';
import {ENTITY_BY_KEY, initialsOf, PaletteHit, routeOfEntity} from '@/components/app/search/searchEntities';
import {RecentVisit, SearchMemoryStore, VISIT_LABELS} from '@/components/app/search/searchStore';
import {canReadArea} from '@/components/app/search/searchAccess';

/** Selectable row of the empty state (no search term) */
export type EmptyEntry = {
    key: string;
    title: string;
    subtitle?: string;
    icon?: string;
    initials?: string;
    to?: RawLocation;
    /** Recent search: puts the term into the input */
    term?: string;
    /** Visits and pins of search areas get a quick view */
    hit?: PaletteHit;
    pinned?: boolean;
};

export type EmptySection = {
    key: 'visits' | 'pins' | 'searches' | 'actions';
    title: string;
    icon: string;
    entries: EmptyEntry[];
};

/** Main pages offered as quick actions, only if the menu item is granted */
const QUICK_ACTION_KEYS: NavigationDrawerAccess[] = [
    NavigationDrawerAccess.TODOS,
    NavigationDrawerAccess.TODAY,
    NavigationDrawerAccess.STUDENTS,
    NavigationDrawerAccess.INSTITUTIONS,
    NavigationDrawerAccess.JOB_ADS_APPLICANTS,
    NavigationDrawerAccess.ACQUISITION_LIST_OVERVIEW,
    NavigationDrawerAccess.TRAININGS,
    NavigationDrawerAccess.COURSES,
    NavigationDrawerAccess.TEACHERS,
    NavigationDrawerAccess.EMPLOYEES,
];
const MAX_QUICK_ACTIONS = 8;

function quickActions(): EmptyEntry[] {
    if (store.state.userType === LoginTypes.PARTNER) {
        return [];
    }
    const granted = NavigationDrawerAccess.getEffectiveAccess((store.state.guiAccess || []) as NavigationDrawerAccess[]);
    const entries: EmptyEntry[] = [];
    for (const key of QUICK_ACTION_KEYS) {
        const data = NavigationDrawerAccess.Record[key];
        const to = data && granted.includes(key) ? (typeof data.to === 'function' ? data.to() : data.to) : null;
        if (data && to) {
            entries.push({key: `action:${key}`, title: data.text, subtitle: 'Seite öffnen', icon: data.icon, to});
        }
    }
    return entries.slice(0, MAX_QUICK_ACTIONS);
}

function ago(time: number): string {
    return moment(time).locale('de').fromNow();
}

/**
 * Quick view hit of a recent visit. Schools of acquisition lists show the quick view of the institution, while
 * "öffnen" keeps opening the acquisition list item; visits without a search area get no quick view.
 */
export function hitOfVisit(visit: RecentVisit): PaletteHit | undefined {
    const base = {id: visit.id, title: visit.title, score: 0, preview: {}};
    if (visit.entity === 'acquisitionListItems') {
        return canReadArea('institutions')
            ? {...base, entity: 'institutions', openTo: visit.path, openLabel: 'Akquise-Standort öffnen'}
            : undefined;
    }
    return ENTITY_BY_KEY[visit.entity] ? {...base, entity: visit.entity} : undefined;
}

export function buildEmptySections(): EmptySection[] {
    // Only areas the user (still) may read; nothing for partner logins
    const visits: EmptyEntry[] = SearchMemoryStore.visits.filter((visit) => canReadArea(visit.entity)).map((visit) => ({
        key: `visit:${visit.entity}:${visit.id}`,
        title: visit.title,
        subtitle: `${VISIT_LABELS[visit.entity] || ''} · ${ago(visit.time)}`,
        initials: initialsOf(visit.title),
        to: visit.path,
        hit: hitOfVisit(visit),
    }));
    const pins: EmptyEntry[] = SearchMemoryStore.pins.filter((pin) => canReadArea(pin.entity)).map((pin) => ({
        key: `pin:${pin.entity}:${pin.id}`,
        title: pin.title,
        subtitle: [ENTITY_BY_KEY[pin.entity]?.singular, pin.subtitle].filter((el) => el).join(' · '),
        initials: initialsOf(pin.title),
        to: routeOfEntity(pin.entity, pin.id) || undefined,
        pinned: true,
        hit: {id: pin.id, entity: pin.entity, title: pin.title, customId: pin.subtitle, score: 0, preview: {}},
    }));
    const searches: EmptyEntry[] = SearchMemoryStore.searches.map((term) => ({
        key: `search:${term}`,
        title: term,
        icon: 'mdi-history',
        term,
    }));
    return [
        {key: 'visits' as const, title: 'Zuletzt besucht', icon: 'mdi-clock-outline', entries: visits},
        {key: 'pins' as const, title: 'Angeheftet', icon: 'mdi-star-outline', entries: pins},
        {key: 'searches' as const, title: 'Letzte Suchen', icon: 'mdi-magnify', entries: searches},
        {key: 'actions' as const, title: 'Schnellaktionen', icon: 'mdi-lightning-bolt-outline', entries: quickActions()},
    ].filter((section) => section.entries.length > 0);
}
